// ✅ REDUX SLICE - getAgentsAvailabilityStats.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import dayjs from 'dayjs';
import { ROOT_URL } from '../../api/ApiConfig';
import {
  applyAnalyticsPendingLoading,
  markAnalyticsFetchKey,
} from '../../utils/analyticsQuery';

const detectRangeType = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const diffDays = end.diff(start, 'day');

  if (start.isSame(end, 'day') && end.isSame(dayjs(), 'day')) return 'today';
  if (start.isSame(end, 'day') && end.isSame(dayjs().subtract(1, 'day'), 'day')) return 'yesterday';

  const now = dayjs();
  if (diffDays === 6 && end.isSame(now, 'day')) return '7d';
  if (diffDays === 29 && end.isSame(now, 'day')) return '30d';
  if (diffDays === 89 && end.isSame(now, 'day')) return '90d';
  if (diffDays === 364 && end.isSame(now, 'day')) return '1y';
  if (start.isSame(now.startOf('week'), 'day') && end.isSame(now, 'day')) return 'week';
  if (start.isSame(now.startOf('month'), 'day') && end.isSame(now, 'day')) return 'month';

  return 'custom';
};

export const getAgentsAvailabilityStats = createAsyncThunk(
  'agentsAvailabilityStats/getAgentsAvailabilityStats',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authToken = state.loginToken?.response?.token;
      const businessId = state.loginToken?.response?.user?.business_id;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Karachi';

      if (!businessId) throw new Error('Business ID not found');

      let startDate = params.startDate;
      let endDate = params.endDate;
      let range = params.range;
      const agentId = params.agent_id; // ✅ Get agent_id from params
      const group_id = params.group_id;

      if (!startDate || !endDate) {
        endDate = dayjs().format('YYYY-MM-DD');
        startDate = dayjs().subtract(6, 'days').format('YYYY-MM-DD');
        range = '7d';
      }

      if (!range) range = detectRangeType(startDate, endDate);

      // ✅ Build query string - only add agent_id if provided
      let query = `timezone=${timezone}&range=${range}`;
      if (range === 'custom') {
        query += `&from=${startDate}&to=${endDate}`;
      }
      // ✅ Only add agent_id if it's provided and not undefined
      if (agentId) {
        query += `&agent_id=${agentId}`;
      }
      if (group_id) {
        query += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const url = `${ROOT_URL}/analytics/agent/availability/${businessId}?${query}`;

      console.log('📊 Requesting Availability Stats From:', url);

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error fetching Availability stats:', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch Availability stats'
      );
    }
  }
);

export const initAgentsAvailabilityStats = createAsyncThunk(
  'agentsAvailabilityStats/initAgentsAvailabilityStats',
  async ({ group_id } = {}, { dispatch }) => {
    const startDate = dayjs().subtract(6, 'day').format('YYYY-MM-DD');
    const endDate = dayjs().format('YYYY-MM-DD');
    // ✅ Don't pass agent_id for initial load - get all agents
    await dispatch(getAgentsAvailabilityStats({ startDate, endDate, range: '7d', group_id }));
  }
);

const initialState = {
  loading: false,
  error: null,
  data: null,
  lastFetchKey: null,
  ticketData: [],
  range: '7d',
  dateRange: {
    startDate: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  },
};

const agentsAvailabilityStatsSlice = createSlice({
  name: 'agentsAvailabilityStats',
  initialState,
  reducers: {
    resetAgentsAvailabilityStats: () => initialState,
    setDateRange: (state, action) => {
      const { startDate, endDate } = action.payload;
      state.dateRange = { startDate, endDate };
      state.range = detectRangeType(startDate, endDate);
    },
    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getAgentsAvailabilityStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, action.meta?.arg);
        state.error = null;
      })
      .addCase(getAgentsAvailabilityStats.fulfilled, (state, action) => {
        state.loading = false;
        markAnalyticsFetchKey(state, action.meta?.arg);
        state.error = null;
        const payload = action.payload;

        if (payload?.data) {
          state.data = payload.data;
          state.ticketData = payload.data.data || payload.data.tags || [];
          state.range = payload.data.range || state.range;
          state.dateRange = {
            startDate: payload.data.from || state.dateRange.startDate,
            endDate: payload.data.to || state.dateRange.endDate,
          };
        } else if (payload?.tags) {
          state.data = payload;
          state.ticketData = payload.tags;
          state.range = payload.range || state.range;
          state.dateRange = {
            startDate: payload.from || state.dateRange.startDate,
            endDate: payload.to || state.dateRange.endDate,
          };
        } else if (Array.isArray(payload)) {
          state.data = { tags: payload };
          state.ticketData = payload;
        } else {
          state.data = payload;
          state.ticketData = [];
        }

        console.log('✅ Availability data loaded:', state.data);
      })
      .addCase(getAgentsAvailabilityStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = null;
        state.ticketData = [];
        console.error('❌ Availability loading failed:', action.payload);
      })
      .addCase(initAgentsAvailabilityStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, { range: '7d', group_id: action.meta?.arg?.group_id });
      })
      .addCase(initAgentsAvailabilityStats.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(initAgentsAvailabilityStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetAgentsAvailabilityStats, setDateRange, clearError } =
  agentsAvailabilityStatsSlice.actions;

export default agentsAvailabilityStatsSlice.reducer;